// CORE IMPORTS
import { useEffect, useState } from "react";
import { SdkParameters } from "onfido-sdk-ui/types/Onfido";

// MATERIAL UI
import { Grid, Stack, Typography } from "@mui/material";

// COMPONENTS
import { LoadingSpinner } from "src/components/application/LoadingSpinner";
import PageLayoutController from "src/components/core/PageLayoutController";
import ComponentLayoutWrapper from "src/components/core/ComponentLayoutWrapper";

// STYLES
import { SignUpFormStyles as SecureBiometricsStyles } from "src/components/auth/styles/SignUpForm.styles";

// REDUX
import api from "src/store/features/api";
import { useAppDispatch } from "src/store/store";
import { setRekycOnfidoCheck, useUserSlice } from "src/store/features/user.slice";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

// HOOKS
import { useOnfidoObserver } from "./useOnfidoObserver";
import { setApplicationStep } from "src/store/features/onboarding.slice";
import { CreateApplicantApiArg } from "src/store/features/api/generated";
import { setCurrentStage, STAGES } from "src/store/features/contactChange.slice";
import { Onfido } from "onfido-sdk-ui";

const DESKTOP_LAYOUT = [
  ["APP_BAR", "APP_BAR", "APP_BAR", "APP_BAR", "APP_BAR", "APP_BAR"],
  ["FORM", "FORM", "FORM", "FORM", "FORM", "FORM"],
];

const MOBILE_LAYOUT = [
  ["APP_BAR", "APP_BAR"],
  ["FORM", "FORM"],
];

/**
 * This page is for public secure biometrics
 * If you are looking for Onfido Implementation check `OnfidoVerification.tsx` instead.
 * @returns {*}
 */
const OnfidoVerification = ({ reason }: CreateApplicantApiArg) => {
  const classes = SecureBiometricsStyles();
  const [sdkToken, setSdkToken] = useState<null | string>();
  const { data, isError, isFetching, isSuccess } = api.useCreateApplicantQuery({
    reason,
  });
  const [error, setError] = useState(isError);
  const [workflowRunId, setWorkFlowId] = useState<string | null>();
  const [loading, setLoading] = useState(false);
  const [updateReKycSteps] = api.useLazyUpdateReKycStepsQuery();
  const [updateOnfidoSteps] = api.useUpdateOnfidoStepsMutation();
  const [updateKYCSteps] = api.useUpdateKycStepsMutation();
  const dispatch = useAppDispatch();

  useOnfidoObserver();

  const {
    reKycDetails: { reKyc },
  } = useUserSlice();

  useEffect(() => {
    setLoading(true);
    if (isSuccess) {
      setSdkToken(data.token);
      setWorkFlowId(data.workflowRunId);
      if (reason === "Contact Change") {
        trackEvent("Contact Change Initiated Onfido", { data }, "Contact Change");
      } else {
        trackEvent("Guest Initiated Onfido", { data }, "Onboarding");
      }
      setLoading(false);
    }
  }, [isSuccess]);

  const contactChangeSdkParams = {
    translations: {
      en_US: {
        welcome: {
          title: "Contact Change Verification",
          subtitle: "This is a verification to change your contact details.",
        },
      },
    },
  };

  useEffect(() => {
    if (sdkToken && workflowRunId) {
      const sdkParams: SdkParameters = {
        token: sdkToken,
        workflowRunId: workflowRunId,
        language: {
          locale: "en_US", // force the language to be English
        },
        ...(reason === "Contact Change" ? contactChangeSdkParams : {}),
        onComplete: async function (data: any) {
          console.warn(
            `USER WITH WORKFLOW_RUN_ID ${workflowRunId} COMPLETED ONFIDO`,
            JSON.stringify(data),
          );
          if (Object.keys(data).length > 0) {
            if (reason === "Contact Change") {
              trackEvent("Contact Change Workflow Completed", { data }, "Contact Change");
              dispatch(setCurrentStage(STAGES.COMPLETED));
              await updateOnfidoSteps();
            } else if (reKyc) {
              await updateReKycSteps();
              trackEvent("Onfido Studio workflow is complete", { data }, "Re KYC");
              dispatch(setRekycOnfidoCheck(true));
            } else {
              console.warn(`WORKFLOW_RUN_ID ${workflowRunId} UPDATING KYC STEPS`, {
                data: JSON.stringify(data),
                workflowRunId,
              });
              await updateKYCSteps({
                updateKycStepsDto: {
                  biometrics: true,
                  workflowRunId,
                },
              });
              trackEvent("Onfido Studio workflow is complete", { data }, "Application Form");
              dispatch(setApplicationStep("aboutYou"));
            }
            // Invalidate the user causing a refetch of user data as soon this step finishes
            dispatch(api.util.invalidateTags(["getUserDataTag"]));
          }
        },
        onError: function (error: any) {
          setError(true);
        },
      };
      Onfido.init(sdkParams);
    }
  }, [sdkToken, workflowRunId]);

  // If stuck on loading your data, SF is taking too long to return user data
  if (loading) {
    if (reason === "Contact Change") {
      return (
        <Grid container display="flex" alignItems="center" justifyContent="center" height="70vh">
          <Stack direction="column" spacing={1} alignItems="center" justifyContent="center">
            <Typography padding={2} align="center">
              Hang tight while we are preparing your Contact Change verification, please do not
              click off this window.
            </Typography>
            <LoadingSpinner size={60} />
          </Stack>
        </Grid>
      );
    } else {
      return (
        <Grid container display="flex" alignItems="center" justifyContent="center" height="70vh">
          <LoadingSpinner size={60} />
        </Grid>
      );
    }
  }

  return (
    <>
      <PageLayoutController
        mobileLayout={MOBILE_LAYOUT}
        desktopLayout={DESKTOP_LAYOUT}
        container={false}>
        <ComponentLayoutWrapper
          gridArea="FORM"
          fadeInDirection="none"
          state={{}}
          loadingComponent={<></>}>
          {(isError || error) && (
            <div className={classes.container} style={{ justifyContent: "center" }}>
              <Grid
                item
                container
                className={classes.disclaimer}
                sx={{ justifyContent: "center", textAlign: "center" }}>
                <Typography className={classes.termsOfUse}>
                  We're sorry. There appears to be an error with your submission. This error has
                  been logged with the relevant team. Please reach out to Support for assistance via
                  the Chat Messenger in the bottom-right of your screen or email
                  support@calebandbrown.com.{" "}
                </Typography>
              </Grid>
            </div>
          )}
        </ComponentLayoutWrapper>

        {isSuccess && !isFetching && (
          <>
            {/* Main Component */}
            <ComponentLayoutWrapper
              gridArea="FORM"
              fadeInDirection="none"
              state={{}}
              loadingComponent={<></>}>
              <div
                style={{ display: "flex", justifyContent: "center", height: "100vh" }}
                id="onfido-mount"></div>
            </ComponentLayoutWrapper>
          </>
        )}
      </PageLayoutController>
    </>
  );
};

export default OnfidoVerification;
